// Adaptador concreto del repositorio de vehicle-lookup (capa HTTP).
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { VehicleLookupRepository } from '../../../domain/repositories/vehicle-lookup/vehicle-lookup.repository';
import {
  UpdateOwnerPayerAddress,
  UpdateOwnerPayerDocument,
  UpdateOwnerPayerInput,
  UpdateSelectedPlanInput,
  VehicleLookupAddress,
  VehicleLookupDocument,
  VehicleLookupInput,
  VehicleLookupPayer,
  VehicleLookupResult,
  VehicleLookupVehicleInfo,
} from '../../../domain/interfaces/vehicle-lookup/vehicle-lookup.interface';
import { HttpClientService } from '../../services/http-client/http-client.service';
import { API_ENDPOINTS } from '../../config/api.config';

interface VehicleLookupRequestDto {
  lead_id: string;
  accept_tos: boolean;
  set_utm_campaign: string;
  set_utm_content: string;
  set_utm_medium: string;
  set_utm_source: string;
  set_utm_term: string;
}

interface VehicleLookupVehicleDto {
  '0KMRunt': boolean;
  Antiguedad: number;
  Caracteristicas: string;
  CatCilin: string;
  Chasis: string;
  CiudadCirculacion: string;
  Clase: string;
  CodDane: string;
  CodFasecolda: string;
  CodigoMarcaLinea: string;
  Combustible: string;
  Electrico: string;
  Financiado: string;
  GPS: string;
  Linea: string;
  Marca: string;
  Modelo: string;
  Motor: string;
  Placa: string;
  Potencia: string;
  PrendaRunt: string;
  Salvamento: string;
  TipoCombustible: string;
  TipoDeUso: string;
  TipoVehiculo: string;
  UsoVehiculo: string;
  ValorAseguradoRunt: number;
  ZonaCirculacion: string;
  ZonaCirculacionId: string;
  ZonaValida: boolean;
  MLinea: string;
  TieneAccesoriosAdicionales: boolean;
  ValorAsegurado: number;
  ValorVehiculo: number;
}

interface VehicleLookupDocumentDto {
  issued: string;
  number: string;
  type: string;
}

interface VehicleLookupAddressDto {
  city_id: string;
  country: string;
  department_id: string;
  street: string;
}

/** Nota: `firsnames` (sin "t") es un typo real de la API, no un error de tipeo nuestro. */
interface VehicleLookupPayerDto {
  born_date: string;
  document: VehicleLookupDocumentDto;
  email: string;
  firsnames: string;
  lastnames: string;
  phone: string;
  residency_address: VehicleLookupAddressDto;
}

interface VehicleLookupResponseDataDto {
  Vehiculo: VehicleLookupVehicleDto;
  accept_tos: boolean;
  address: VehicleLookupAddressDto;
  born_date: string;
  document: VehicleLookupDocumentDto;
  email: string;
  firsnames: string;
  lastnames: string;
  lead_id: string;
  payer: VehicleLookupPayerDto;
  phone: string;
  zonaCirculacion: string;
}


interface UpdateOwnerPayerRequestPersonDto {
  born_date: string;
  document: VehicleLookupDocumentDto;
  email: string;
  firstname: string;
  lastname: string;
  mothername: string;
  phone: string;
  surname: string;
}

interface UpdateOwnerPayerRequestDto {
  lead_id: string;
  accept_tos: boolean;
  address: VehicleLookupAddressDto;
  born_date: string;
  document: VehicleLookupDocumentDto;
  email: string;
  firstname: string;
  lastname: string;
  mothername: string;
  phone: string;
  surname: string;
  payer: UpdateOwnerPayerRequestPersonDto & { residency_address: VehicleLookupAddressDto };
  prendario: string;
  assessor_code?: string;
  set_utm_campaign: string;
  set_utm_content: string;
  set_utm_medium: string;
  set_utm_source: string;
  set_utm_term: string;
}


interface UpdateSelectedPlanRequestDto {
  lead_id: string;
  accept_tos: boolean;
  productCode: string;
  payment_frequency: string;
  set_utm_campaign: string;
  set_utm_content: string;
  set_utm_medium: string;
  set_utm_source: string;
  set_utm_term: string;
}

@Injectable()
export class VehicleLookupRepositoryImplementation extends VehicleLookupRepository {
  private readonly http = inject(HttpClientService);

  override getVehicleData(input: VehicleLookupInput): Observable<VehicleLookupResult> {
    const dto: VehicleLookupRequestDto = {
      lead_id: input.leadId,
      accept_tos: input.acceptTos,
      set_utm_campaign: input.utmCampaign ?? '',
      set_utm_content: input.utmContent ?? '',
      set_utm_medium: input.utmMedium ?? '',
      set_utm_source: input.utmSource ?? '',
      set_utm_term: input.utmTerm ?? '',
    };

    return this.http
      .put<VehicleLookupResponseDataDto>(API_ENDPOINTS.vehicleLookup.get(input.leadId), dto)
      .pipe(map((response) => this.toResult(response.data)));
  }

  override updateOwnerAndPayer(input: UpdateOwnerPayerInput): Observable<VehicleLookupResult> {
    const dto: UpdateOwnerPayerRequestDto = {
      lead_id: input.leadId,
      accept_tos: input.acceptTos,
      address: this.toAddressDto(input.owner.address),
      born_date: input.owner.bornDate,
      document: this.toDocumentDto(input.owner.document),
      email: input.owner.email,
      firstname: input.owner.firstName,
      lastname: input.owner.lastName,
      mothername: input.owner.motherName,
      phone: input.owner.phone,
      surname: input.owner.surname,
      payer: {
        born_date: input.payer.bornDate,
        document: this.toDocumentDto(input.payer.document),
        email: input.payer.email,
        firstname: input.payer.firstName,
        lastname: input.payer.lastName,
        mothername: input.payer.motherName,
        phone: input.payer.phone,
        residency_address: this.toAddressDto(input.payer.address),
        surname: input.payer.surname,
      },
      prendario: input.prendario ?? '',
      set_utm_campaign: input.utmCampaign ?? '',
      set_utm_content: input.utmContent ?? '',
      set_utm_medium: input.utmMedium ?? '',
      set_utm_source: input.utmSource ?? '',
      set_utm_term: input.utmTerm ?? '',
      ...(input.assessorCode ? { assessor_code: input.assessorCode } : {}),
    };

    return this.http
      .put<VehicleLookupResponseDataDto>(API_ENDPOINTS.vehicleLookup.get(input.leadId), dto)
      .pipe(map((response) => this.toResult(response.data)));
  }

  override updateSelectedPlan(input: UpdateSelectedPlanInput): Observable<VehicleLookupResult> {
    const dto: UpdateSelectedPlanRequestDto = {
      lead_id: input.leadId,
      accept_tos: input.acceptTos,
      productCode: input.productCode,
      payment_frequency: input.paymentFrequency,
      set_utm_campaign: input.utmCampaign ?? '',
      set_utm_content: input.utmContent ?? '',
      set_utm_medium: input.utmMedium ?? '',
      set_utm_source: input.utmSource ?? '',
      set_utm_term: input.utmTerm ?? '',
    };

    return this.http
      .put<VehicleLookupResponseDataDto>(API_ENDPOINTS.vehicleLookup.get(input.leadId), dto)
      .pipe(map((response) => this.toResult(response.data)));
  }

  private toAddressDto(address: UpdateOwnerPayerAddress): VehicleLookupAddressDto {
    return {
      city_id: address.cityId,
      country: address.country,
      department_id: address.departmentId,
      street: address.street,
    };
  }

  private toDocumentDto(document: UpdateOwnerPayerDocument): VehicleLookupDocumentDto {
    return { issued: document.issued, number: document.number, type: document.type };
  }

  private normalizeIsoDate(value: string): string {
    const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value);
    if (!match) return value;
    const [, year, month, day] = match;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  private toResult(data: VehicleLookupResponseDataDto): VehicleLookupResult {
    return {
      leadId: data.lead_id,
      acceptTos: data.accept_tos,
      vehicle: this.toVehicleInfo(data.Vehiculo),
      address: this.toAddress(data.address),
      bornDate: this.normalizeIsoDate(data.born_date),
      document: this.toDocument(data.document),
      email: data.email,
      firstNames: data.firsnames,
      lastNames: data.lastnames,
      phone: data.phone,
      payer: this.toPayer(data.payer),
      circulationZoneName: data.zonaCirculacion,
    };
  }

  private toVehicleInfo(vehicle: VehicleLookupVehicleDto): VehicleLookupVehicleInfo {
    return {
      is0KmRunt: vehicle['0KMRunt'],
      age: vehicle.Antiguedad,
      characteristics: vehicle.Caracteristicas,
      catCilin: vehicle.CatCilin,
      chassis: vehicle.Chasis,
      circulationCity: vehicle.CiudadCirculacion,
      vehicleClass: vehicle.Clase,
      daneCode: vehicle.CodDane,
      fasecoldaCode: vehicle.CodFasecolda,
      brandLineCode: vehicle.CodigoMarcaLinea,
      fuel: vehicle.Combustible,
      isElectric: vehicle.Electrico,
      isFinanced: vehicle.Financiado,
      hasGps: vehicle.GPS,
      line: vehicle.Linea,
      brand: vehicle.Marca,
      model: vehicle.Modelo,
      engine: vehicle.Motor,
      plate: vehicle.Placa,
      power: vehicle.Potencia,
      pledgeRunt: vehicle.PrendaRunt,
      salvage: vehicle.Salvamento,
      fuelType: vehicle.TipoCombustible,
      useType: vehicle.TipoDeUso,
      vehicleType: vehicle.TipoVehiculo,
      usageType: vehicle.UsoVehiculo,
      insuredValueRunt: vehicle.ValorAseguradoRunt,
      circulationZone: vehicle.ZonaCirculacion,
      circulationZoneId: vehicle.ZonaCirculacionId,
      isValidZone: vehicle.ZonaValida,
      brandLineDescription: vehicle.MLinea,
      hasAdditionalAccessories: vehicle.TieneAccesoriosAdicionales,
      insuredValue: vehicle.ValorAsegurado,
      vehicleValue: vehicle.ValorVehiculo,
    };
  }

  private toDocument(document: VehicleLookupDocumentDto): VehicleLookupDocument {
    return { issued: document.issued, number: document.number, type: document.type };
  }

  private toAddress(address: VehicleLookupAddressDto): VehicleLookupAddress {
    return {
      cityId: address.city_id,
      country: address.country,
      departmentId: address.department_id,
      street: address.street,
    };
  }

  private toPayer(payer: VehicleLookupPayerDto): VehicleLookupPayer {
    return {
      bornDate: this.normalizeIsoDate(payer.born_date),
      document: this.toDocument(payer.document),
      email: payer.email,
      firstNames: payer.firsnames,
      lastNames: payer.lastnames,
      phone: payer.phone,
      residencyAddress: this.toAddress(payer.residency_address),
    };
  }
}
